/**
 * Instance Info Section - User's card instance data (quantity, condition, etc.)
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoAdd, IoDocument, IoFolder, IoPricetag, IoRemove, IoTime } from 'react-icons/io5';

import { CONDITION_LABELS, FINISH_LABELS, LANGUAGE_LABELS, SOURCE_LABELS } from '@/lib/cardUtils';
import { formatPurchasePrice } from '@/lib/price';
import type { EnrichedCollectionCard } from '@/types/card';
import { useUpdateCardMutation } from '../../hooks/api';

export interface InstanceInfoSectionProps {
  card: EnrichedCollectionCard;
}

/** Condition badge colors */
const CONDITION_COLORS: Record<string, string> = {
  M: 'bg-success/20 text-success',
  NM: 'bg-success/20 text-success',
  LP: 'bg-gold/20 text-gold',
  MP: 'bg-mythic/20 text-mythic',
  HP: 'bg-danger/20 text-danger',
  D: 'bg-danger/20 text-danger',
};

export default function InstanceInfoSection({ card }: InstanceInfoSectionProps): ReactElement {
  const updateMutation = useUpdateCardMutation();

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, card.quantity + delta);
    if (newQuantity !== card.quantity) {
      updateMutation.mutate({
        cardId: card.id,
        data: { quantity: newQuantity },
      });
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTimestamp = (timestamp: { toDate?: () => Date } | null): string => {
    if (!timestamp || !timestamp.toDate) return '—';
    return timestamp.toDate().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <section className="px-6 pb-6">
      <div className="bg-slate/50 border-storm rounded-xl border p-5">
        {/* Section Header */}
        <h3 className="text-ash mb-4 text-xs font-medium tracking-widest uppercase">Your Copy</h3>

        {/* Quantity + Attributes Row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Quantity Controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleQuantityChange(-1)}
              disabled={card.quantity <= 1 || updateMutation.isPending}
              className="bg-slate border-storm text-silver hover:text-pearl disabled:text-ash flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Decrease quantity"
            >
              <IoRemove className="h-4 w-4" />
            </button>
            <span className="text-pearl min-w-10 text-center font-mono text-lg font-medium">{card.quantity}</span>
            <button
              onClick={() => handleQuantityChange(1)}
              disabled={updateMutation.isPending}
              className="bg-slate border-storm text-silver hover:text-pearl disabled:text-ash flex h-8 w-8 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed"
              aria-label="Increase quantity"
            >
              <IoAdd className="h-4 w-4" />
            </button>
          </div>

          {/* Condition Badge */}
          <span
            className={clsx('rounded-full px-2.5 py-1 text-xs font-medium', CONDITION_COLORS[card.condition] ?? 'bg-slate text-silver')}
          >
            {CONDITION_LABELS[card.condition]}
          </span>

          {/* Finish */}
          <span className="text-silver text-sm">
            {card.finish === 'foil' && '✨ '}
            {FINISH_LABELS[card.finish]}
          </span>

          {/* Language */}
          <span className="text-silver text-sm">{LANGUAGE_LABELS[card.language]}</span>
        </div>

        {/* Info Rows */}
        <div className="mt-4 space-y-3">
          {/* Folder */}
          <div className="flex items-center gap-2 text-sm">
            <IoFolder className="text-ash h-4 w-4 shrink-0" />
            <span className="text-silver">{card.folder_path ?? 'Unfiled'}</span>
          </div>

          {/* Purchase Info */}
          {(card.purchase_price || card.purchase_date) && (
            <div className="flex items-center gap-2 text-sm">
              <IoPricetag className="text-ash h-4 w-4 shrink-0" />
              <span className="text-silver">
                {card.purchase_price != null && (
                  <span className="font-mono">{formatPurchasePrice(card.purchase_price, card.purchase_currency)}</span>
                )}
                {card.purchase_price != null && card.purchase_date && ' • '}
                {card.purchase_date && formatDate(card.purchase_date)}
              </span>
            </div>
          )}

          {/* Notes */}
          {card.notes && (
            <div className="flex items-start gap-2 text-sm">
              <IoDocument className="text-ash mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-silver italic">{card.notes}</span>
            </div>
          )}

          {/* Source + Timestamps */}
          <div className="flex items-center gap-2 text-sm">
            <IoTime className="text-ash h-4 w-4 shrink-0" />
            <span className="text-ash">
              {SOURCE_LABELS[card.source_system]}
              {' • Added '}
              {formatTimestamp(card.created_at)}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
